import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { ALL_ROLES, CLINEASE_ADMINISTRATOR } from './util/RoleUtil';

import { createStaffMember, retrieveLoggedInSystemUser } from './services/StaffMemberService';
import { retrieveTemporaryObject, saveTemporaryObject } from './services/TemporaryStorageService';

import { View } from './components/View';
import { Spinner } from './components/common/spinner/Spinner';

export const App = ({ keycloak }) => {
  const [context, setContext] = useState();
  const [spinnerText, setSpinnerText] = useState(null);

  const username = useRef(keycloak.idTokenParsed.preferred_username).current;
  const keycloakRoles = useRef(keycloak.realmAccess.roles).current;

  const createNewSystemUser = useCallback(async () => {
    const systemUserToCreate = {
      firstName: keycloak.idTokenParsed.given_name,
      lastName: keycloak.idTokenParsed.family_name,
      userName: keycloak.idTokenParsed.preferred_username,
      emailAddress: keycloak.idTokenParsed.email,
      organisation: keycloak.idTokenParsed.organisation,
      userRoles: keycloak.idTokenParsed.organisationRole,
      active: true,
    };

    const [data, error] = await createStaffMember('clineaseadmin', systemUserToCreate);

    if (data) {
      if (data.responseStatus !== 'Success') {
        console.error(data.responseMessage);
      }
    } else {
      console.error(error);
    }
  }, [keycloak]);

  const checkAccess = useCallback(() => {
    if (!keycloakRoles || keycloakRoles.length === 0) {
      return false;
    }

    for (let i = 0; i < keycloakRoles.length; i++) {
      const keycloakRole = keycloakRoles[i];

      if (ALL_ROLES.includes(keycloakRole)) {
        return true;
      }
    }

    setContext({
      viewStack: [],
      breadcrumbs: [],
      username,
      roles: keycloakRoles,
      currentScreen: 'noAccess',
    });

    setSpinnerText(null);

    return false;
  }, [keycloakRoles, username]);

  const buildDefaultContext = useCallback(
    (loggedInUser) => {
      let currentScreen = 'dashboard';
      let activeSection = 'Dashboard';

      if (keycloakRoles.includes(CLINEASE_ADMINISTRATOR)) {
        currentScreen = 'usersTable';
        activeSection = 'Administration';
      }

      return {
        viewStack: [],
        breadcrumbs: [],
        username,
        roles: keycloakRoles,
        loggedInUser,
        currentScreen,
        activeSection,
      };
    },
    [keycloakRoles, username]
  );

  const getLinkParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);

    const reviewExternalReference = params.get('reviewExternalReference');
    const queryExternalReference = params.get('queryExternalReference');

    if (!reviewExternalReference || !queryExternalReference) {
      return null;
    }

    return {
      reviewExternalReference,
      queryExternalReference,
    };
  }, []);

  const clearLinkParams = useCallback(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const initialiseContext = useCallback(async () => {
    setSpinnerText('Retrieving Logged In User');

    const hasAccess = checkAccess();

    if (!hasAccess) {
      return;
    }

    const [data, error] = await retrieveLoggedInSystemUser(username);

    if (data) {
      if (data.responseStatus === 'Success') {
        const deepLink = getLinkParams();
        const baseContext = buildDefaultContext(data.systemUserVO);

        if (deepLink) {
          setContext({
            ...baseContext,
            currentScreen: 'participantReviewsEdit',
            activeTab: 'participantReviewCreateQuery',
            additionalContext: {
              reviewExternalReference: deepLink.reviewExternalReference,
            },
            notification: {
              contextType: 'Query',
              contextReference: `${deepLink.reviewExternalReference}$${deepLink.queryExternalReference}`,
            },
            viewStack: [
              {
                screen: 'participantReviewsEdit',
                externalReference: {
                  reviewExternalReference: deepLink.reviewExternalReference,
                },
              },
            ],
          });

          clearLinkParams();
        } else {
          setContext(baseContext);
        }
      } else {
        await createNewSystemUser();
        await initialiseContext();
      }
    } else {
      console.error(error);
    }
  }, [buildDefaultContext, checkAccess, clearLinkParams, createNewSystemUser, getLinkParams, username]);

  useEffect(() => {
    setSpinnerText('Retrieving session');

    const hasAccess = checkAccess();

    if (!hasAccess) {
      return;
    }

    const fetchData = async () => {
      const link = getLinkParams();

      if (link) {
        const [loggedInUserData, loggedInUserError] = await retrieveLoggedInSystemUser(username);

        if (loggedInUserData) {
          if (loggedInUserData.responseStatus === 'Success') {
            const baseContext = buildDefaultContext(loggedInUserData.systemUserVO);

            setContext({
              ...baseContext,
              currentScreen: 'participantReviewsEdit',
              activeTab: 'participantReviewCreateQuery',
              additionalContext: {
                reviewExternalReference: link.reviewExternalReference,
              },
              notification: {
                contextType: 'Query',
                contextReference: `${link.reviewExternalReference}$${link.queryExternalReference}`,
              },
              viewStack: [
                {
                  screen: 'participantReviewsEdit',
                  externalReference: {
                    reviewExternalReference: link.reviewExternalReference,
                  },
                },
              ],
            });

            clearLinkParams();
          } else {
            await createNewSystemUser();
            await initialiseContext();
          }
        } else {
          console.error(loggedInUserError);
        }

        setSpinnerText(null);
        return;
      }

      const [data] = await retrieveTemporaryObject(username);

      if (data) {
        if (data.responseStatus === 'Success') {
          const retrievedContext = JSON.parse(data.temporaryStorageVOToView.value);

          setContext({
            ...retrievedContext,
            roles: keycloakRoles,
          });
        } else {
          await initialiseContext();
        }
      } else {
        await initialiseContext();
      }

      setSpinnerText(null);
    };

    fetchData();
  }, [
    username,
    keycloakRoles,
    initialiseContext,
    checkAccess,
    getLinkParams,
    clearLinkParams,
    buildDefaultContext,
    createNewSystemUser,
  ]);

  useEffect(() => {
    if (context && context.currentScreen !== 'noAccess') {
      const saveData = async () => {
        let contextToPersist = {
          ...context,
          notification: undefined,
        };

        if (context.currentScreen === 'participantReviewsEdit') {
          const reviewExternalReference =
            context.additionalContext?.reviewExternalReference ||
            context.viewStack?.find((item) => item?.screen === 'participantReviewsEdit')?.externalReference
              ?.reviewExternalReference;

          contextToPersist = {
            ...contextToPersist,
            activeTab: 'participantReviewCreateGeneral',
            additionalContext: reviewExternalReference ? { reviewExternalReference } : context.additionalContext,
            viewStack: [
              {
                screen: 'participantReviewsEdit',
                externalReference: reviewExternalReference
                  ? { reviewExternalReference }
                  : context.additionalContext || null,
              },
            ],
          };
        }

        await saveTemporaryObject(username, {
          key: username,
          value: JSON.stringify(contextToPersist),
        });
      };

      saveData();
    }
  }, [context, username]);

  useEffect(() => {
    localStorage.setItem('token', keycloak.token);

    const tokenDuration = keycloak.tokenParsed.exp * 1000 - Date.now();

    const timeout = setTimeout(() => {
      localStorage.removeItem('token');
      keycloak.logout();
    }, tokenDuration);

    return () => {
      clearTimeout(timeout);
    };
  }, [keycloak]);

  useIdleTimer({
    timeout: 1000 * 60 * 30,
    onIdle: () => keycloak.logout(),
  });

  return (
    <div>
      {context && (
        <View
          keycloak={keycloak}
          context={context}
          setContext={setContext}
          spinnerText={spinnerText}
          setSpinnerText={setSpinnerText}
          toolTipsEnabled={context?.loggedInUser?.toolTipsEnabled}
        />
      )}

      <Spinner loadingText={spinnerText} />
    </div>
  );
};
